import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { FloatingMenuContainer } from '@/components/layout/FloatingMenuContainer';
import { useDebouncedFn } from '@/hooks/useDebouncedFn';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useOptionalDocumentChatContext } from '@/modules/chat/contexts/DocumentChatProvider';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { useIsDocumentWorkflowTrackerInProgress } from '@/modules/workflowTrackers/hooks/useIsDocumentWorkflowTrackerInProgress';
import { WorkflowTrackerKind } from '@/types/schema';

import { useAnnotatedPDFViewerContext } from '../PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';
import { DocumentFooterProps } from '../PDFViewer/DocumentFooter';
import { DocumentSearchBarUI } from './DocumentSearchBarUI';
import { useSearchDocumentWithResponseLazyQuery } from './graphql/DocumentSearch.generated';

export function DocumentSearchBar({ isDocumentLoading }: DocumentFooterProps) {
  const trackUserEvent = useTrackUserEvent();
  const enableAiCapabilities = useAICapabilitiesEnabled();
  const chatContext = useOptionalDocumentChatContext();

  const {
    documentId,
    annotations,
    setAnnotations,
    activeAnnotationIndex,
    setActiveAnnotationIndex,
    searchQuery,
    setSearchQuery,
    onUpdateSearchQuery,
    searchLoading,
    showCopyCitationsButton,
  } = useAnnotatedPDFViewerContext();

  const [paragraphIds, setParagraphIds] = useState<string[]>([]);
  const containerElementRef = useRef<HTMLDivElement>(null);
  const [positioningYOffset, setPositioningYOffset] = useState<null | number>(
    0
  );
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [searchResponse, setSearchResponse] = useState<string>('');
  const [searchWithResponseQuery] = useSearchDocumentWithResponseLazyQuery();

  const handleSearch = useCallback(async () => {
    const startTime = Date.now();
    if (!documentId) {
      // We should never get here, but just in case and for type safety.
      throw new Error('Document ID is required to search');
    }
    if (!searchQuery.length) return;

    // Reset various state variables
    setLoadingSearch(true);
    setAnnotations(undefined);
    setActiveAnnotationIndex(0);
    setSearchError(false);
    setSearchResponse('');
    onUpdateSearchQuery?.();

    await searchWithResponseQuery({
      variables: {
        input: {
          documentID: documentId,
          query: searchQuery,
          first: 1,
        },
      },
      onCompleted: (data) => {
        trackUserEvent('document searched', {
          query: searchQuery,
          success: true,
          duration: Date.now() - startTime,
        });
        const results = data.queryDocument.boundingRegions || [];
        const boundingRegions = results.flatMap((result) => {
          return {
            ...result,
            documentID: documentId,
          };
        });
        setAnnotations(boundingRegions);
        setSearchResponse(data.queryDocument.response);
        setParagraphIds(data.queryDocument.paragraphIds);
      },
      onError: () => {
        trackUserEvent('document searched', {
          query: searchQuery,
          success: false,
          duration: Date.now() - startTime,
        });
        setParagraphIds([]);
        setAnnotations(undefined);
        setSearchError(true);
      },
    });
    setLoadingSearch(false);
  }, [
    documentId,
    searchQuery,
    setAnnotations,
    setActiveAnnotationIndex,
    onUpdateSearchQuery,
    searchWithResponseQuery,
    trackUserEvent,
  ]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchError(false);
    setAnnotations(undefined);
    setActiveAnnotationIndex(0);
    setSearchResponse('');
    onUpdateSearchQuery?.();
  }, [
    onUpdateSearchQuery,
    setActiveAnnotationIndex,
    setAnnotations,
    setSearchQuery,
  ]);

  const handleDismiss = useCallback(() => {
    setAnnotations(undefined);
  }, [setAnnotations]);

  const debouncedSetPositioningYOffset = useDebouncedFn(
    (nextOffset: number) => {
      setPositioningYOffset(nextOffset);
    },
    50
  );

  // Use workflow tracker to determine if document extraction is complete
  const isDocumentExtractionInProgress = useIsDocumentWorkflowTrackerInProgress(
    documentId,
    WorkflowTrackerKind.DocumentSummary
  );

  const isDocumentChatEnabled = !!chatContext && enableAiCapabilities;

  useLayoutEffect(() => {
    if (!containerElementRef.current) return;

    const options = {
      threshold: [0.01, 0.25, 0.5, 0.75, 1],
    };

    // The observer looks for the intersection of the search bar and the viewport.
    // It is updated as the user scrolls the page.
    const observer = new IntersectionObserver((entries) => {
      const firstEntry = entries[0];
      if (!firstEntry?.rootBounds) return;
      const searchBarHeight = firstEntry.boundingClientRect.height;
      const viewportHeight = firstEntry.rootBounds.height;
      const searchbarDistanceFromTopPx = firstEntry.boundingClientRect.y;
      // Calculate how far the search bar needs to be translated to be visible in the viewport
      const proposedNextYOffset = -(
        searchbarDistanceFromTopPx -
        viewportHeight +
        searchBarHeight +
        16
      );

      // We only want to translate the search bar up to be within view.
      // It cannot go past it's original position at the bottom of the PDFViewer.
      const nextYOffset = Math.ceil(Math.min(0, proposedNextYOffset));

      // De-dupe and debounce to prevent unnecessary re-renders
      if (nextYOffset !== positioningYOffset) {
        debouncedSetPositioningYOffset(nextYOffset);
      }
    }, options);

    observer.observe(containerElementRef.current);

    return () => {
      observer.disconnect();
    };
  }, [debouncedSetPositioningYOffset, positioningYOffset]);

  const hasCitationBeenClicked =
    annotations !== undefined && annotations.length > 0;

  if (
    isDocumentLoading ||
    !documentId ||
    !enableAiCapabilities || // Never show search bar if tenant has disabled AI capabilities
    isDocumentExtractionInProgress || // Don't show search bar until document extraction is complete
    (isDocumentChatEnabled && !hasCitationBeenClicked) // Hide by default in document chat until citation is clicked
  ) {
    return null;
  }

  return (
    <FloatingMenuContainer ref={containerElementRef} offset={{ bottom: 16 }}>
      <DocumentSearchBarUI
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onClearSearch={handleClearSearch}
        loadingSearch={loadingSearch}
        searchLoading={searchLoading}
        searchError={searchError}
        searchResponse={searchResponse}
        paragraphIds={paragraphIds}
        annotations={annotations}
        activeAnnotationIndex={activeAnnotationIndex}
        setActiveAnnotationIndex={setActiveAnnotationIndex}
        showCopyCitationsButton={showCopyCitationsButton}
        positioningYOffset={positioningYOffset}
        onDismiss={handleDismiss}
      />
    </FloatingMenuContainer>
  );
}
